import React, { useCallback, useEffect, useState } from 'react'
import 'css/high-contrast.css'

const STORAGE_KEY = 'afo-high-contrast'

/* Reads the stored preference without throwing. Private-mode browsers and
   locked-down school devices can make localStorage itself throw on access,
   and a student's lesson must not fail to render over a display preference. */
const readStored = () => {
	try {
		return window.localStorage.getItem(STORAGE_KEY) === '1'
	} catch {
		return false
	}
}

/* Owns the body class and the stored preference. Deliberately per device
   rather than broadcast from the teacher's board: a student who needs high
   contrast needs it whether or not the teacher turned it on. */
export const useHighContrast = () => {
	const [on, setOn] = useState(readStored)

	useEffect(() => {
		document.body.classList.toggle('high-contrast', on)
		try {
			window.localStorage.setItem(STORAGE_KEY, on ? '1' : '0')
		} catch {
			/* preference just won't persist; the toggle still works this session */
		}
		return () => document.body.classList.remove('high-contrast')
	}, [on])

	const toggle = useCallback(() => setOn(prev => !prev), [])

	return { highContrast: on, toggleHighContrast: toggle }
}

export const HighContrastToggle = ({ on, onToggle, className = '' }) => (
	<button
		type="button"
		className={`hc-toggle ${className}`}
		onClick={onToggle}
		aria-pressed={on}
		title={on ? 'Turn off high contrast' : 'Turn on high contrast'}
	>
		<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
			<circle cx="12" cy="12" r="9" />
			<path d="M12 3v18" />
			<path d="M12 3a9 9 0 0 1 0 18z" fill="currentColor" stroke="none" />
		</svg>
		High contrast
	</button>
)
